import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

const PRESETS_PERSONAL = [10, 25, 50, 100];
const PRESETS_BUSINESS = [100, 250, 500, 1000];

export default function SupportModal({ open, onClose, athlete, onSuccess, initialMode = "personal", initialFrequency = "one-off" }) {
  const [mode, setMode] = useState(initialMode); // "personal" | "business"
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [type, setType] = useState(initialFrequency);
  const [message, setMessage] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [company, setCompany] = useState("");
  const [logo, setLogo] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [loading, setLoading] = useState(false);

  // Reset internal state when reopened
  React.useEffect(() => {
    if (open) {
      setMode(initialMode);
      setType(initialFrequency);
      setAmount(initialMode === "business" ? 250 : 50);
      setCustom("");
      setMessage("");
      setAnonymous(false);
      setCompany("");
      setLogo("");
      setContactEmail("");
    }
  }, [open, initialMode, initialFrequency]);

  if (!open || !athlete) return null;

  const presets = mode === "business" ? PRESETS_BUSINESS : PRESETS_PERSONAL;
  const finalAmount = custom !== "" ? Number(custom) : amount;

  const changeMode = (next) => {
    setMode(next);
    setAmount(next === "business" ? 250 : 50);
    setCustom("");
  };

  const submit = async () => {
    if (!finalAmount || finalAmount <= 0) {
      toast.error("Please choose an amount");
      return;
    }
    if (mode === "business" && (!company.trim() || !contactEmail.trim())) {
      toast.error("Company name and contact email are required");
      return;
    }
    setLoading(true);
    try {
      if (mode === "business") {
        await api.post("/sponsorships", {
          athlete_id: athlete.athlete_id,
          amount: finalAmount,
          type,
          company_name: company.trim(),
          logo_url: logo || null,
          contact_email: contactEmail.trim(),
        });
        toast.success("Sponsorship confirmed (mocked). Your logo will appear on the profile shortly.");
      } else {
        await api.post("/donations", {
          athlete_id: athlete.athlete_id,
          amount: finalAmount,
          type,
          anonymous,
          message: message || null,
        });
        toast.success("Thank you, your contribution has been received (mocked payment).");
      }
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(mode === "business" ? "Sponsorship failed. Please try again." : "Donation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const firstName = athlete.name.split(" ")[0];

  return (
    <div
      className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="support-modal-overlay"
    >
      <div
        className="bg-background w-full md:max-w-lg border border-border max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="support-modal"
      >
        <div className="p-6 md:p-8">
          <div className="overline mb-2">Support the journey</div>
          <h3 className="font-display text-3xl">
            Back <span className="text-primary">{athlete.name}</span>
          </h3>

          {/* PERSONAL / BUSINESS TOGGLE */}
          <div className="mt-6" data-testid="support-mode-toggle">
            <div className="overline mb-3">Supporting as</div>
            <div className="grid grid-cols-2 gap-0 border border-border">
              {[
                { key: "personal", label: "Personal" },
                { key: "business", label: "Business" },
              ].map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => changeMode(m.key)}
                  className={`py-3 text-sm tracking-wide transition-colors ${
                    mode === m.key ? "bg-primary text-primary-foreground" : "bg-transparent hover:bg-muted"
                  }`}
                  data-testid={`support-mode-${m.key}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-foreground/60">
              {mode === "business"
                ? `Your company name and logo will appear on ${firstName}'s profile.`
                : `100% goes towards ${firstName}'s 2026 season. Payments securely processed (mocked in preview).`}
            </p>
          </div>

          {/* FREQUENCY */}
          <div className="mt-6">
            <div className="overline mb-3">Frequency</div>
            <div className="grid grid-cols-2 gap-0 border border-border">
              {["one-off", "monthly"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`py-3 text-sm tracking-wide transition-colors ${
                    type === t ? "bg-primary text-primary-foreground" : "bg-transparent hover:bg-muted"
                  }`}
                  data-testid={`support-type-${t}`}
                >
                  {t === "one-off" ? "One-off" : "Monthly"}
                </button>
              ))}
            </div>
          </div>

          {/* AMOUNT */}
          <div className="mt-6">
            <div className="overline mb-3">Amount (EUR)</div>
            <div className="grid grid-cols-4 gap-0 border border-border">
              {presets.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => { setAmount(p); setCustom(""); }}
                  className={`py-3 text-base font-medium transition-colors border-l first:border-l-0 border-border ${
                    !custom && amount === p ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                  }`}
                  data-testid={`support-amount-${p}`}
                >
                  €{p}
                </button>
              ))}
            </div>
            <input
              type="number"
              min="1"
              placeholder="Custom amount"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              className="mt-3 w-full h-12 px-4 border border-border bg-background outline-none focus:border-primary"
              data-testid="support-custom-amount"
            />
          </div>

          {/* CONDITIONAL FIELDS */}
          {mode === "business" ? (
            <div className="mt-6 space-y-3" data-testid="support-business-fields">
              <input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Company / brand name"
                className="w-full h-12 px-4 border border-border bg-background outline-none focus:border-primary"
                data-testid="support-company"
              />
              <input
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="Contact email"
                type="email"
                className="w-full h-12 px-4 border border-border bg-background outline-none focus:border-primary"
                data-testid="support-email"
              />
              <input
                value={logo}
                onChange={(e) => setLogo(e.target.value)}
                placeholder="Logo URL (optional)"
                className="w-full h-12 px-4 border border-border bg-background outline-none focus:border-primary"
                data-testid="support-logo"
              />
            </div>
          ) : (
            <div className="mt-6" data-testid="support-personal-fields">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Add a short message (optional)"
                rows={3}
                className="w-full p-3 border border-border bg-background outline-none focus:border-primary text-sm"
                data-testid="support-message"
              />
              <label className="flex items-center gap-2 mt-3 text-sm">
                <input
                  type="checkbox"
                  checked={anonymous}
                  onChange={(e) => setAnonymous(e.target.checked)}
                  data-testid="support-anonymous"
                />
                Support anonymously
              </label>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between gap-3">
            <button type="button" onClick={onClose} className="btn-outline" data-testid="support-cancel-btn">
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={loading}
              className="btn-primary flex-1"
              data-testid="support-confirm-btn"
            >
              {loading
                ? "Processing…"
                : `${mode === "business" ? "Sponsor" : "Support"} €${finalAmount || 0}${type === "monthly" ? "/month" : ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
